import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faComments, faGear, faPaperPlane, faPlugCircleXmark, faRobot } from "@fortawesome/free-solid-svg-icons"
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons"
import { useLocation } from "react-router-dom"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import {
  useWhatsAppAiMessageSettings,
  defaultWhatsAppAiMessageSettings,
} from "../../../hooks/WhatsAppAutomation/useWhatsAppAiMessageSettings"
import type { IWhatsAppAiMessageSettings } from "../../../Objects/WhatsAppAutomation/WhatsAppAiMessageSettings"
import { WhatsAppMessageSettingsPopup } from "./components/WhatsAppMessageSettingsPopup"
import { WhatsAppBotSettingsPopup } from "./components/WhatsAppBotSettingsPopup"
import { useWhatsAppCrm } from "../../../hooks/WhatsAppAutomation/useWhatsAppCrm"
import type { IWhatsAppCrmConversation, IWhatsAppCrmMessage } from "../../../Objects/WhatsAppAutomation/WhatsAppCrm"
import type { IWhatsAppAgentOverviewDto } from "../../../Objects/WhatsAppAutomation/WhatsAppAgentOverview"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { CanView } from "../../../components/CanView/CanView"
import { Permissions } from "../../../Enum/PermissionsType"
import { useEmployerLogged } from "../../../context/useEmployeeContext"
import { useQuickActionPopup } from "../../../hooks/useQuickActionPopup"

export const WhatsAppCrmPage: React.FC = () => {
  const { t } = useTranslation(["pages", "helpPopup"])

  const { getSettings, saveSettings } = useWhatsAppAiMessageSettings()

  const { getConversations, getMessages, getCrmOverview, sendCrmMessage } = useWhatsAppCrm()

  const location = useLocation()

  const { userEmployee } = useEmployerLogged()

  const [settings, setSettings] = useState<IWhatsAppAiMessageSettings>(defaultWhatsAppAiMessageSettings)

  const [isSettingsPopupOpen, setIsSettingsPopupOpen] = useState(false)

  const [isBotSettingsPopupOpen, setIsBotSettingsPopupOpen] = useState(false)

  const [agents, setAgents] = useState<IWhatsAppAgentOverviewDto[]>([])

  const [conversations, setConversations] = useState<IWhatsAppCrmConversation[]>([])

  const [messages, setMessages] = useState<IWhatsAppCrmMessage[]>([])

  const [selectedConversation, setSelectedConversation] = useState<IWhatsAppCrmConversation | null>(null)

  const [messageBody, setMessageBody] = useState("")

  const [isSendingMessage, setIsSendingMessage] = useState(false)

  const messagesEndRef = useRef<HTMLDivElement | null>(null)

  const isAgentReady = agents.some((agent) => agent.isOnline && agent.isProcessHealthy && agent.isWhatsAppReady)

  const canConfigureWhatsApp =
    userEmployee?.isMaster || userEmployee?.permissions?.includes(Permissions.CanEditeCompany)

  useQuickActionPopup(
    "whatsapp-crm-message-settings",
    () => {
      setIsSettingsPopupOpen(true)
    },
    !!canConfigureWhatsApp,
  )

  useQuickActionPopup(
    "whatsapp-crm-bot-settings",
    () => {
      setIsBotSettingsPopupOpen(true)
    },
    !!canConfigureWhatsApp,
  )

  const load = async (preferredRemotePhone?: string | null) => {
    if (canConfigureWhatsApp) {
      setSettings(await getSettings())
    }

    const loadedAgents = await getCrmOverview()

    const hasReadyAgent = loadedAgents.some(
      (agent) => agent.isOnline && agent.isProcessHealthy && agent.isWhatsAppReady,
    )

    setAgents(loadedAgents)

    const loadedConversations = hasReadyAgent ? await getConversations() : []
    const preferredConversation = preferredRemotePhone
      ? (loadedConversations.find(
          (conversation) =>
            conversation.remotePhone === preferredRemotePhone || conversation.remoteChatId === preferredRemotePhone,
        ) ?? {
          remotePhone: preferredRemotePhone,
          remoteChatId: preferredRemotePhone,
          remoteName: null,
          lastMessage: "",
          lastMessageAt: new Date().toISOString(),
          messageCount: 0,
          unreadCount: 0,
        })
      : null

    setConversations(
      preferredConversation
        ? loadedConversations.map((conversation) =>
            conversation.remotePhone === preferredConversation.remotePhone
              ? { ...conversation, unreadCount: 0 }
              : conversation,
          )
        : loadedConversations,
    )
    setSelectedConversation(preferredConversation)

    setMessages(hasReadyAgent && preferredConversation ? await getMessages(preferredConversation.remotePhone) : [])
  }

  useEffect(() => {
    const remotePhone = new URLSearchParams(location.search).get("remotePhone")

    void load(remotePhone)
  }, [location.search])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" })
  }, [messages, selectedConversation?.remotePhone])

  const handleSave = async () => {
    setSettings(await saveSettings(settings))

    setIsSettingsPopupOpen(false)

    setIsBotSettingsPopupOpen(false)
  }

  const handleSelectConversation = async (conversation: IWhatsAppCrmConversation) => {
    if (!isAgentReady) {
      return
    }

    setSelectedConversation(conversation)

    setMessages(await getMessages(conversation.remotePhone))
    setConversations((current) =>
      current.map((item) => (item.remotePhone === conversation.remotePhone ? { ...item, unreadCount: 0 } : item)),
    )
  }

  const appendManualMessage = (conversation: IWhatsAppCrmConversation, body: string) => {
    const timestamp = new Date().toISOString()
    const manualMessage: IWhatsAppCrmMessage = {
      providerMessageId: `manual-${conversation.remotePhone}-${Date.now()}`,
      remotePhone: conversation.remotePhone,
      remoteChatId: conversation.remoteChatId,
      remoteName: conversation.remoteName,
      body,
      fromMe: true,
      timestamp,
    }

    setMessages((current) => [...current, manualMessage])

    setSelectedConversation((current) =>
      current?.remotePhone === conversation.remotePhone
        ? {
            ...current,
            lastMessage: body,
            lastMessageAt: timestamp,
            messageCount: current.messageCount + 1,
          }
        : current,
    )

    setConversations((current) =>
      current
        .map((item) =>
          item.remotePhone === conversation.remotePhone
            ? {
                ...item,
                lastMessage: body,
                lastMessageAt: timestamp,
                messageCount: item.messageCount + 1,
                unreadCount: 0,
              }
            : item,
        )
        .sort((a, b) => Date.parse(b.lastMessageAt) - Date.parse(a.lastMessageAt)),
    )
  }

  const handleSendMessage = async () => {
    const body = messageBody.trim()

    const agent = agents.find((item) => item.isOnline && item.isProcessHealthy && item.isWhatsAppReady)

    if (!selectedConversation || !body || !agent || isSendingMessage) {
      return
    }

    setIsSendingMessage(true)

    try {
      await sendCrmMessage({
        agentId: agent.id,
        toPhone: selectedConversation.remoteChatId || selectedConversation.remotePhone,
        body,
      })

      appendManualMessage(selectedConversation, body)
      setMessageBody("")
    } finally {
      setIsSendingMessage(false)
    }
  }

  return (
    <div className="h-[calc(100%-25px)] w-full max-w-full overflow-x-hidden overflow-y-auto p-2 sm:p-4 md:p-2">
      <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
        <div className="w-full max-w-full overflow-x-hidden">
          <div className="mb-3 mt-3 flex flex-row items-start justify-between p-2 ">
            <h2 className="flex items-center gap-2 text-lg font-bold text-slate-800 sm:text-xl">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                <FontAwesomeIcon icon={faWhatsapp} className="text-sm" />
              </span>

              {t("label_whatsapp_crm_title")}
            </h2>

            <PopupHelpButton
              variant="global"
              helpKey="pages.whatsapp-crm"
              title={t("label_whatsapp_crm_title")}
              description={t("label_whatsapp_crm_subtitle")}
              bullets={t("helpPopup:global.whatsAppCrm.bullets", { returnObjects: true }) as string[]}
              steps={t("helpPopup:global.whatsAppCrm.steps", { returnObjects: true }) as string[]}
            />
          </div>

          <div className="w-full max-w-full overflow-x-hidden">
            <div className="mb-4 grid gap-2 sm:flex sm:flex-wrap">
              <CanView permission={Permissions.CanEditeCompany}>
                <button
                  type="button"
                  onClick={() => setIsSettingsPopupOpen(true)}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-700 shadow-sm transition hover:bg-emerald-100 sm:w-auto"
                  data-interaction-description-key="interactionDescriptions:pages.myRestaurant.whatsAppCrm.whatsAppCrmPage.button.setIsSettingsPopupOpenTrue"
                >
                  <FontAwesomeIcon icon={faGear} />
                  {t("label_whatsapp_message_settings_open")}
                </button>

                <button
                  type="button"
                  onClick={() => setIsBotSettingsPopupOpen(true)}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-sky-300 bg-sky-50 px-4 py-2 text-sm font-bold text-sky-700 shadow-sm transition hover:bg-sky-100 sm:w-auto"
                  data-interaction-description-key="interactionDescriptions:pages.myRestaurant.whatsAppCrm.whatsAppCrmPage.button.setIsBotSettingsPopupOpenTrue"
                >
                  <FontAwesomeIcon icon={faRobot} />
                  {t("label_whatsapp_bot_settings_open")}
                </button>
              </CanView>

              <button
                type="button"
                onClick={() => void load()}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 sm:w-auto"
                data-interaction-description-key="interactionDescriptions:pages.myRestaurant.whatsAppCrm.whatsAppCrmPage.button.voidLoad"
              >
                <FontAwesomeIcon icon={faComments} />
                {t("label_whatsapp_crm_refresh")}
              </button>
            </div>

            {!isAgentReady && (
              <Animate type={AnimationTypes.slideUpIn} duration="0.45s">
                <div className="mt-4 rounded-lg border-2 border-amber-200 bg-amber-50 p-4 text-amber-950">
                  <div className="flex flex-col gap-3 text-center sm:flex-row sm:items-start sm:text-left">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center self-center rounded-lg bg-white text-amber-700 sm:self-start">
                      <FontAwesomeIcon icon={faPlugCircleXmark} />
                    </span>

                    <div className="min-w-0">
                      <strong className="block text-sm">{t("label_whatsapp_crm_agent_offline_title")}</strong>

                      <p className="mt-1 text-xs text-justify font-medium leading-relaxed">
                        {t("label_whatsapp_crm_agent_offline_description")}
                      </p>
                    </div>
                  </div>
                </div>
              </Animate>
            )}

            {isAgentReady && (
              <div className="mt-4 grid w-full max-w-full min-w-0 grid-cols-1 gap-3 overflow-x-hidden lg:h-[calc(100vh-250px)] lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)]">
                <section className="flex h-[260px] min-w-0 max-w-full flex-col overflow-hidden rounded-lg border-2 border-slate-200 bg-white p-3 shadow-sm sm:h-[300px] lg:h-full">
                  <strong className="block text-sm text-slate-900">
                    {t("label_whatsapp_crm_conversations_title")}
                  </strong>

                  <p className="mt-1 max-w-full truncate text-xs text-slate-500">
                    {t("label_whatsapp_crm_conversations_description")}
                  </p>

                  <div className="mt-3 grid min-h-0 max-w-full flex-1 content-start gap-2 overflow-x-hidden overflow-y-auto pr-1">
                    {conversations.length === 0 && (
                      <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-center text-xs text-slate-500">
                        {t("label_whatsapp_crm_no_conversations")}
                      </div>
                    )}

                    {conversations.map((conversation) => (
                      <button
                        key={conversation.remotePhone}
                        type="button"
                        onClick={() => void handleSelectConversation(conversation)}
                        className={`min-w-0 max-w-full overflow-hidden rounded-lg border-2 p-3 text-left transition ${
                          selectedConversation?.remotePhone === conversation.remotePhone
                            ? "border-emerald-300 bg-emerald-50"
                            : "border-slate-100 bg-white hover:border-slate-200 hover:bg-slate-50"
                        }`}
                        data-interaction-description-key="interactionDescriptions:pages.myRestaurant.whatsAppCrm.whatsAppCrmPage.button.voidHandleSelectConversationConversation"
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          {conversation.unreadCount > 0 && (
                            <span
                              className="h-2.5 w-2.5 shrink-0 rounded-full bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.16)]"
                              title={t("label_whatsapp_crm_unread_messages", { count: conversation.unreadCount })}
                              aria-label={t("label_whatsapp_crm_unread_messages", { count: conversation.unreadCount })}
                            />
                          )}

                          <strong className="block min-w-0 flex-1 truncate text-sm text-slate-900">
                            {conversation.remoteName || conversation.remotePhone}
                          </strong>
                        </div>

                        <span className="mt-1 block max-w-full truncate text-xs text-slate-500">
                          {conversation.lastMessage}
                        </span>

                        <span className="mt-2 block max-w-full truncate text-[11px] font-semibold text-emerald-700">
                          {t("label_whatsapp_crm_message_count", { count: conversation.messageCount })}
                        </span>
                      </button>
                    ))}
                  </div>
                </section>

                <section className="flex h-[70vh] min-h-[520px] min-w-0 max-w-full flex-col overflow-hidden rounded-lg border-2 border-slate-200 bg-slate-50 p-3 shadow-sm lg:h-full lg:min-h-0">
                  <strong className="block text-sm text-slate-900">
                    {selectedConversation?.remoteName ||
                      selectedConversation?.remotePhone ||
                      t("label_whatsapp_crm_messages_title")}
                  </strong>

                  <p className="mt-1 max-w-full truncate text-xs text-slate-500">
                    {t("label_whatsapp_crm_messages_description")}
                  </p>

                  <div className="mt-3 flex min-h-0 max-w-full flex-1 flex-col gap-2 overflow-x-hidden overflow-y-auto rounded-lg bg-white p-3">
                    {!selectedConversation && (
                      <div className="m-auto max-w-sm text-center text-xs text-slate-500">
                        {t("label_whatsapp_crm_select_conversation")}
                      </div>
                    )}

                    {selectedConversation && messages.length === 0 && (
                      <div className="m-auto max-w-sm text-center text-xs text-slate-500">
                        {t("label_whatsapp_crm_no_messages")}
                      </div>
                    )}

                    {messages.map((message) => (
                      <div
                        key={message.providerMessageId}
                        className={`flex ${message.fromMe ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[82%] wrap-break-word rounded-lg px-3 py-2 text-xs leading-relaxed shadow-sm ${
                            message.fromMe ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-700"
                          }`}
                        >
                          {message.body}
                        </div>
                      </div>
                    ))}

                    <div ref={messagesEndRef} />
                  </div>

                  <div className="mt-3 flex min-w-0 max-w-full flex-col gap-2 sm:flex-row">
                    <textarea
                      value={messageBody}
                      onChange={(event) => setMessageBody(event.target.value)}
                      disabled={!selectedConversation || !isAgentReady || isSendingMessage}
                      placeholder={t("label_whatsapp_crm_reply_placeholder")}
                      className="min-h-[46px] min-w-0 max-w-full flex-1 resize-none rounded-lg border-2 border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 outline-none transition focus:border-emerald-400 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                      data-interaction-description-key="interactionDescriptions:pages.myRestaurant.whatsAppCrm.whatsAppCrmPage.input.whatsappCrmReplyPlaceholder"
                    />

                    <button
                      type="button"
                      onClick={() => void handleSendMessage()}
                      disabled={!selectedConversation || !messageBody.trim() || !isAgentReady || isSendingMessage}
                      className="inline-flex min-h-[46px] w-full items-center justify-center gap-2 rounded-lg border-2 border-emerald-500 bg-emerald-600 px-4 py-2 text-sm font-black text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-300 sm:w-auto"
                      data-interaction-description-key="interactionDescriptions:pages.myRestaurant.whatsAppCrm.whatsAppCrmPage.button.voidHandleSendMessage"
                    >
                      <FontAwesomeIcon icon={faPaperPlane} />
                      {isSendingMessage ? t("label_whatsapp_crm_sending") : t("label_whatsapp_crm_send")}
                    </button>
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
      </Animate>

      <WhatsAppMessageSettingsPopup
        isOpen={isSettingsPopupOpen}
        settings={settings}
        onClose={() => setIsSettingsPopupOpen(false)}
        onChange={setSettings}
        onSave={() => void handleSave()}
      />

      <WhatsAppBotSettingsPopup
        isOpen={isBotSettingsPopupOpen}
        settings={settings}
        onClose={() => setIsBotSettingsPopupOpen(false)}
        onChange={setSettings}
        onSave={() => void handleSave()}
      />
    </div>
  )
}
